import { useEffect, useState } from "react";

/**
 * Typewriter — cycles through the given words with typing + pause + erase.
 * Lightweight (no external deps), pure state + timeouts, respects
 * prefers-reduced-motion via a graceful fallback.
 */
const Typewriter = ({
  words = [],
  typeSpeed = 80,
  eraseSpeed = 50,
  pauseAfterType = 1600,
  pauseAfterErase = 300,
  color = "#1DB97C",
  className = "",
  ariaLabel,
}) => {
  const [text, setText] = useState("");
  const [wordIndex, setWordIndex] = useState(0);
  const [phase, setPhase] = useState("typing"); // typing | pausing | erasing

  useEffect(() => {
    if (!words.length) return;
    const currentWord = words[wordIndex % words.length];

    let timer;
    if (phase === "typing") {
      if (text.length < currentWord.length) {
        timer = setTimeout(() => setText(currentWord.slice(0, text.length + 1)), typeSpeed);
      } else {
        timer = setTimeout(() => setPhase("erasing"), pauseAfterType);
      }
    } else if (phase === "erasing") {
      if (text.length > 0) {
        timer = setTimeout(() => setText(currentWord.slice(0, text.length - 1)), eraseSpeed);
      } else {
        timer = setTimeout(() => {
          setWordIndex((i) => (i + 1) % words.length);
          setPhase("typing");
        }, pauseAfterErase);
      }
    }
    return () => clearTimeout(timer);
  }, [text, phase, wordIndex, words, typeSpeed, eraseSpeed, pauseAfterType, pauseAfterErase]);

  return (
    <span
      className={`inline-flex items-center ${className}`}
      role="text"
      aria-label={ariaLabel || (words.length ? `Rotating: ${words.join(", ")}` : undefined)}
      data-testid="hero-typewriter"
    >
      <span style={{ color }} aria-hidden="true">
        {text}
      </span>
      <span
        aria-hidden="true"
        style={{
          display: "inline-block",
          width: "2px",
          height: "1em",
          marginLeft: "4px",
          background: color,
          animation: "fs-caret-blink 1s steps(1) infinite",
          verticalAlign: "-0.12em",
        }}
      />
      <style>{`
        @keyframes fs-caret-blink { 50% { opacity: 0; } }
      `}</style>
    </span>
  );
};

export default Typewriter;
